import { Box, Divider, Link, Stack, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createGitlabIntegration,
  getGitlabIntegrationLink,
  getGitLabRepositories,
  getGitLabRepositoryBranches,
  integrateProjectWithGitHubRepo,
  qk,
  updateGitLabRepository,
} from 'api'
import { IconGitLab } from 'assets/icons'
import { DialogContent, ToastContent } from 'elements/feedback'
import { Autocomplete, renderTextField } from 'elements/inputs'
import { LinkButton } from 'elements/navigation'
import { getInputError, useValidations } from 'lib/form-utils'
import { useSearch } from 'lib/hooks'
import { useInfinitePaginatedQuery } from 'lib/react-query-utils'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { IntegrationModalHeader } from '../integration-modal-header'

const formId = 'CREATE_GITLAB_INTEGRATION_FORM'

type FormValues = {
  baseBranch: string
  pathTemplate: string
  repo: string
}

type CreateGitLabIntegrationFormProps = {
  projectId: string
  closeDialog: () => void
  code: string | null
}

export const CreateGitLabIntegrationForm = ({
  projectId,
  closeDialog,
  code,
}: CreateGitLabIntegrationFormProps) => {
  const { t } = useTranslation()

  const { search: searchBranches, onChange: onBranchSearch } = useSearch()
  const { search: searchRepos, onChange: onReposSearch } = useSearch()

  const queryClient = useQueryClient()

  const rules = useValidations()

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      baseBranch: '',
      pathTemplate: '',
      repo: '',
    },
  })

  const repo = watch('repo')
  const branch = watch('baseBranch')

  useEffect(() => {
    setValue('baseBranch', '')
  }, [setValue, repo])

  const $updateGitLabRepository = useMutation(updateGitLabRepository)

  const $integrateProjectWithGitHubRepo = useMutation(
    integrateProjectWithGitHubRepo,
  )

  const $createGitlabIntegration = useQuery(
    qk.gitlab.createIntegration.toKeyWithArgs({ code: code ?? '', projectId }),
    () => createGitlabIntegration({ code: code ?? '', projectId }),
    {
      enabled: code !== null,
      onSuccess: () => {
        toast.success(
          <ToastContent
            title={t(
              'projects.settings.integrations.gitlab_integration_is_installed',
            )}
          />,
        )
      },
      onError: () => {
        toast.error(
          <ToastContent
            title={t('client_errors.failed_to_create_integration')}
          />,
        )
        closeDialog()
      },
    },
  )

  const repoId = $createGitlabIntegration.data?.repoId

  const $gitlabIntegrationLink = useQuery(
    qk.projects.gitlabLink.toKeyWithArgs({ projectId }),
    () => getGitlabIntegrationLink({ projectId }),
  )

  const $gitlabRepositories = useInfinitePaginatedQuery(
    qk.gitlab.repositories,
    getGitLabRepositories,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      projectId,
      repoId: repoId ?? '',
      search: searchRepos,
    },
    {
      enabled: projectId !== '' && $createGitlabIntegration.data !== undefined,
      onError: error => {
        toast.error(
          <ToastContent title={(error as { message: string }).message} />,
        )
      },
    },
  )

  const $gitlabBranches = useInfinitePaginatedQuery(
    qk.gitlab.branches,
    getGitLabRepositoryBranches,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      projectId,
      repoId: repoId ?? '',
      repoName: repo,
      search: searchBranches,
    },
    {
      enabled: repo !== '' && $createGitlabIntegration.data !== undefined,
      onError: error => {
        toast.error(
          <ToastContent title={(error as { message: string }).message} />,
        )
      },
    },
  )

  return $updateGitLabRepository.status === 'success' ? (
    <DialogContent onDeny={closeDialog} hideCancelButton>
      <Stack alignItems="center" spacing={2}>
        <IconGitLab sx={{ fontSize: '60px', color: 'success.main' }} />
        <Typography variant="body3" fontWeight={600}>
          {t(
            'projects.settings.integrations.gitlab_integrated_with_your_project',
          )}
        </Typography>
        {$updateGitLabRepository.data && (
          <Typography variant="caption2">
            <Trans
              i18nKey="projects.settings.integrations.you_have_gitlab_integration_with_repo"
              values={{
                link: $updateGitLabRepository.data.url,
              }}
              components={{
                linkElement: (
                  <Typography
                    component={Link}
                    variant="inherit"
                    color="inherit"
                    href={$updateGitLabRepository.data.url}
                    target="_blank"
                    underline="hover"
                  />
                ),
              }}
            />
          </Typography>
        )}
      </Stack>
    </DialogContent>
  ) : (
    <DialogContent
      title={t('projects.settings.integrations.create_gitlab_integration')}
      isActionLoading={$integrateProjectWithGitHubRepo.isLoading}
      onDeny={closeDialog}
      externalFormId={formId}
      confirmText={t('common.save')}
    >
      <Divider sx={{ my: 2 }} />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <IntegrationModalHeader
          title={t('projects.settings.integrations.gitlab_integration_warning')}
        />

        <LinkButton
          disabled={!$gitlabIntegrationLink.data || code !== null}
          to={$gitlabIntegrationLink.data ?? ''}
        >
          {t('projects.settings.integrations.integrate_with_gitlab')}
        </LinkButton>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Stack
        id={formId}
        component="form"
        onSubmit={handleSubmit(values =>
          $updateGitLabRepository.mutate(
            {
              projectId,
              repoId: $createGitlabIntegration.data?.repoId!,
              ...values,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(
                  qk.projects.reposList.toKeyWithArgs({ projectId }),
                )
              },
            },
          ),
        )}
        spacing={2}
        mt={2}
      >
        <Controller
          name="repo"
          control={control}
          rules={rules.required}
          render={({ field, fieldState }) => (
            <Autocomplete
              label={t('projects.settings.integrations.repository_name.gitlab')}
              disableInternalFilter
              disableAlphabeticalOrder
              options={
                $gitlabRepositories.data?.pages.flatMap(page =>
                  page.rows.map(repository => ({
                    value: repository,
                    label: repository,
                  })),
                ) ?? []
              }
              hasMoreOptions={$gitlabRepositories.hasNextPage}
              onLoadMore={$gitlabRepositories.fetchNextPage}
              isLoadingMore={
                $gitlabRepositories.isFetchingNextPage ||
                $gitlabRepositories.isLoading
              }
              disabled={!$createGitlabIntegration.data}
              {...getInputError(fieldState.error)}
              {...field}
              onInputChange={onReposSearch}
            />
          )}
        />

        <Controller
          name="baseBranch"
          control={control}
          rules={rules.required}
          render={({ field, fieldState }) => (
            <Autocomplete
              label={t('projects.settings.integrations.base_branch')}
              disableInternalFilter
              disableAlphabeticalOrder
              options={
                $gitlabBranches.data?.pages.flatMap(page =>
                  page.rows.map(branch => ({
                    value: branch,
                    label: branch,
                  })),
                ) ?? []
              }
              hasMoreOptions={$gitlabBranches.hasNextPage}
              onLoadMore={$gitlabBranches.fetchNextPage}
              isLoadingMore={
                $gitlabBranches.isFetchingNextPage || $gitlabBranches.isLoading
              }
              disabled={repo === ''}
              {...getInputError(fieldState.error)}
              {...field}
              onInputChange={onBranchSearch}
            />
          )}
        />

        <Controller
          name="pathTemplate"
          control={control}
          rules={{ ...rules.pathTemplate, ...rules.required }}
          render={renderTextField({
            disabled: repo === '' || branch === '',
            label: t('projects.settings.integrations.path_template'),
          })}
        />
      </Stack>
    </DialogContent>
  )
}
